import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { GridServerDatasource, GridSortModelItem } from './types'

/** Marks a row the grid is still waiting on, so it can draw a skeleton. */
export const GRID_LOADING_ROW = '__gridLoadingRow'

export const isLoadingRow = (row: unknown): boolean =>
  Boolean(row && typeof row === 'object' && (row as Record<string, unknown>)[GRID_LOADING_ROW])

export const loadingRowId = (index: number) => `__loading-${index}`

const makeLoadingRow = <T,>(index: number): T =>
  ({ [GRID_LOADING_ROW]: true, __index: index }) as unknown as T

export interface UseServerSideRowsArgs<T> {
  datasource?: GridServerDatasource<T>
  blockSize: number
  sortModel: GridSortModelItem[]
  filterModel: Record<string, unknown>
  transformBlock?: (rows: T[]) => T[]
  /** Called when the cache is purged, so the viewport can go back to the top. */
  onPurge?: () => void
}

/**
 * Block-cache row model — the replacement for ag-grid's infinite row model.
 *
 * The grid asks for a window of rows; this fetches the blocks that window
 * touches and hands back an array of `totalCount` length, with placeholder rows
 * standing in for anything not fetched yet. That is what lets the scrollbar be
 * the right size from the first response, and what makes a jump to row 4,000
 * fetch only the block around it.
 *
 * A sort or filter change means the row at any given index is now a different
 * row, so the cache is purged and the viewport returns to the top. A refresh
 * without purge keeps the loaded blocks on screen while it refetches them, so
 * an update arriving from elsewhere in the app does not throw away the user's
 * scroll position.
 */
export const useServerSideRows = <T,>({
  datasource,
  blockSize,
  sortModel,
  filterModel,
  transformBlock,
  onPurge,
}: UseServerSideRowsArgs<T>) => {
  const [blocks, setBlocks] = useState<Record<number, T[]>>({})
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Every fetch carries the generation it was started in. A purge bumps the
  // generation, so responses for the old sort/filter are dropped instead of
  // landing on top of the new ones.
  const generationRef = useRef(0)
  const inFlightRef = useRef<Set<number>>(new Set())
  const blocksRef = useRef(blocks)
  blocksRef.current = blocks

  const datasourceRef = useRef(datasource)
  datasourceRef.current = datasource
  const sortModelRef = useRef(sortModel)
  sortModelRef.current = sortModel
  const filterModelRef = useRef(filterModel)
  filterModelRef.current = filterModel
  const transformRef = useRef(transformBlock)
  transformRef.current = transformBlock
  const onPurgeRef = useRef(onPurge)
  onPurgeRef.current = onPurge

  const fetchBlock = useCallback(
    (blockIndex: number) => {
      const source = datasourceRef.current
      if (!source) return
      if (inFlightRef.current.has(blockIndex)) return

      const generation = generationRef.current
      inFlightRef.current.add(blockIndex)
      setIsLoading(true)

      const settle = () => {
        inFlightRef.current.delete(blockIndex)
        if (inFlightRef.current.size === 0) setIsLoading(false)
      }

      source.getRows({
        startRow: blockIndex * blockSize,
        endRow: (blockIndex + 1) * blockSize,
        sortModel: sortModelRef.current,
        filterModel: filterModelRef.current,
        success: (rows, count) => {
          settle()
          if (generation !== generationRef.current) return

          const transform = transformRef.current
          const block = transform ? transform(rows) : rows
          // Mirrored onto the ref straight away: `ensureRange` runs from scroll
          // handlers that fire before React has committed the new state, and
          // would otherwise request the same block again.
          blocksRef.current = { ...blocksRef.current, [blockIndex]: block }
          setBlocks((prev) => ({ ...prev, [blockIndex]: block }))
          if (typeof count === 'number') setTotalCount(count)
        },
        fail: () => {
          settle()
          // The block stays unloaded on purpose: scrolling back over it retries.
        },
      })
    },
    [blockSize],
  )

  const purge = useCallback(() => {
    generationRef.current += 1
    inFlightRef.current.clear()
    blocksRef.current = {}
    setBlocks({})
    setTotalCount(null)
    setIsLoading(false)
    onPurgeRef.current?.()
  }, [])

  // A new sort, filter or datasource identity renumbers every row.
  const sortKey = JSON.stringify(sortModel)
  const filterKey = JSON.stringify(filterModel)
  const firstRunRef = useRef(true)

  useEffect(() => {
    if (!datasource) return

    if (firstRunRef.current) {
      firstRunRef.current = false
    } else {
      purge()
    }
    fetchBlock(0)
    // `sortKey` / `filterKey` stand in for the models: a fresh object with the
    // same contents is not a change worth purging the cache over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasource, sortKey, filterKey, purge, fetchBlock])

  const rows = useMemo<T[]>(() => {
    // The list reaches only as far as the blocks that have loaded, plus one
    // block of runway. Sizing it to the full totalCount instead gave a
    // scrollbar for all ~1000 rows the moment the first block landed, which
    // reads as "everything is here" — and scrolling anywhere in that range
    // fired a fetch for wherever you happened to land. Growing as blocks
    // arrive is the behaviour the station list has: a short bar that extends
    // as you scroll, and rows requested in order.
    //
    // The runway block is what keeps it moving: it renders as placeholders,
    // and scrolling into them is what asks for the next fetch.
    const loaded = Object.keys(blocks).map(Number)
    const highestLoaded = loaded.length > 0 ? Math.max(...loaded) : -1
    const reach = (highestLoaded + 2) * blockSize
    const count =
      totalCount == null ? blockSize : Math.min(totalCount, Math.max(reach, blockSize))
    const out: T[] = new Array(count)

    for (let index = 0; index < count; index++) {
      const block = blocks[Math.floor(index / blockSize)]
      const row = block?.[index % blockSize]
      out[index] = row ?? makeLoadingRow<T>(index)
    }

    return out
  }, [blocks, totalCount, blockSize])

  /** Fetches whatever blocks the given row window touches. */
  const ensureRange = useCallback(
    (startIndex: number, endIndex: number) => {
      if (!datasourceRef.current) return
      const first = Math.max(0, Math.floor(startIndex / blockSize))
      const last = Math.floor(Math.max(startIndex, endIndex) / blockSize)

      for (let block = first; block <= last; block++) {
        if (blocksRef.current[block]) continue
        fetchBlock(block)
      }
    },
    [blockSize, fetchBlock],
  )

  const refresh = useCallback(
    (options?: { purge?: boolean }) => {
      if (options?.purge) {
        purge()
        fetchBlock(0)
        return
      }

      // Keep the rows on screen and swap each block as its response lands, so
      // the scroll position and row count never move under the user.
      generationRef.current += 1
      inFlightRef.current.clear()
      const loaded = Object.keys(blocksRef.current).map(Number)
      if (loaded.length === 0) {
        fetchBlock(0)
        return
      }
      loaded.forEach(fetchBlock)
    },
    [fetchBlock, purge],
  )

  return {
    rows,
    totalCount,
    isLoading,
    /** True until the first block has arrived — the empty state must not show yet. */
    hasLoadedOnce: totalCount !== null,
    ensureRange,
    refresh,
    purge,
  }
}
